import { useAuth0 } from '@auth0/auth0-react';

const externalUserContextStorageName = 'external_user_context';

export interface ExternalUserContext {
  idToken: string,
  user: any,
  productCodes: string[],
  loanData: string,
  requestTypes: string[],
  isIceUser?: boolean,
  isMlinkUser?: boolean,
  extTransactionId?: string,
}

// This component demonstrates how to manually set an ID token
// when it's passed via query parameters (skipping the OAuth flow)
const ManualAuth0Token = () => {

 const auth0Context = useAuth0();

 // Extract token from URL query parameters
 const queryParams = new URLSearchParams(window.location.search);
 const idToken = queryParams.get('idToken');
 const productCodes = queryParams.get('productCodes');
 const requestTypes = queryParams.get('requestTypes');
 const loanData = queryParams.get('loanData');
 const orderId = queryParams.get('orderId');

 if (idToken) {
   const payload = idToken.split('.')[1];
   const decodedToken = JSON.parse(atob(payload));
   localStorage.setItem(externalUserContextStorageName, JSON.stringify({
     idToken,
     user: decodedToken,
     productCodes: productCodes ? productCodes.split(',') : [],
     loanData,
     requestTypes: requestTypes ? requestTypes.split(',') : [],
     isIceUser: true
   }));

 }

  // Override the Auth0 context with our manual authentication
  const externalUserContextJson = localStorage.getItem(externalUserContextStorageName)
  const user = externalUserContextJson ? JSON.parse(externalUserContextJson).user : auth0Context.user;
  const isAuthenticated = user || auth0Context.isAuthenticated;

  if (isAuthenticated) {
    // Redirect to the root URL to remove the token from the query parameters
    window.location.href = orderId ? ('/orders/' + orderId) : '/orders/create';
    return () => {};
  }

  return null;
};

const ManualAuth0TokenMlink = () => {
  const auth0Context = useAuth0();

  const queryParams = new URLSearchParams(window.location.search);
  const idToken = queryParams.get("token");
  const sessionId = queryParams.get("sessionId");
  const mlinkTransactionId = queryParams.get("transactionId");
 
  if (idToken) {
    const payload = idToken.split('.')[1];
    const decodedToken = JSON.parse(atob(payload));
    localStorage.setItem(externalUserContextStorageName, JSON.stringify({
      idToken,
      user: decodedToken,
      isMlinkUser: true,
      extTransactionId: mlinkTransactionId,
    }));
  }

  const externalUserContextJson = localStorage.getItem(externalUserContextStorageName)
  const user = externalUserContextJson ? JSON.parse(externalUserContextJson).user : auth0Context.user;
  const isAuthenticated = user || auth0Context.isAuthenticated;

  if (isAuthenticated) {
    window.location.href = '/orders/create?sessionId=' + sessionId;
    return () => { };
  }

  return null;
};

// Create a wrapper for useAuth0 that includes our manual auth capabilities
export const useXpanseAuth = () => {
  const auth0Context = useAuth0();
  const externalUserContextJson = localStorage.getItem(externalUserContextStorageName)
  const externalUserContext = externalUserContextJson ? JSON.parse(externalUserContextJson) as ExternalUserContext : null;
  const user = externalUserContextJson ? JSON.parse(externalUserContextJson).user : auth0Context.user;


  // Return an enhanced Auth0 context with our manual auth capabilities
  const res = {
    ...auth0Context,
    user: user ? user : auth0Context.user,
    isAuthenticated: user || auth0Context.isAuthenticated,
    isIceUser: externalUserContext?.isIceUser ? true : false,
    isMlinkUser: externalUserContext?.isMlinkUser ? true : false,
    getExternalUserContext: () => externalUserContext,
    getAccessTokenSilently: async (options: any) => {
      if (externalUserContext) {
        return externalUserContext.idToken;
      }
      return auth0Context.getAccessTokenSilently(options);
    },
    isEmbedded: () => externalUserContext ? true : false,
    idToken: async () => externalUserContext ? externalUserContext.idToken : (await auth0Context.getIdTokenClaims())?.__raw,
    logout: async (params:any) => {

      if (externalUserContext) {
        localStorage.removeItem(externalUserContextStorageName);
        window.location.reload();
      }
      await auth0Context.logout();
    }
  };

  return res;
};

export { ManualAuth0Token, ManualAuth0TokenMlink };
